import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import SignatureCanvas from 'react-signature-canvas';

export interface SignaturePadHandle {
	toDataURL: (type?: string) => string;
	toTrimmedDataURL: (margin?: number, type?: string) => string;
	clear: () => void;
	isEmpty: () => boolean;
}

interface Props {
	width?: number;
	height?: number;
}

export const SignaturePad = forwardRef<SignaturePadHandle, Props>(({ width = 400, height = 150 }, ref) => {
	const canvasRef = useRef<SignatureCanvas>(null);

	useImperativeHandle(ref, () => ({
		toDataURL: (type = 'image/png') => canvasRef.current?.toDataURL(type) ?? '',
		toTrimmedDataURL: (margin = 20, type = 'image/png') => {
			const pad = canvasRef.current;
			if (!pad || pad.isEmpty()) {
				return '';
			}

			// Tight bounding box around the strokes, then re-pad with a transparent margin.
			const trimmed = pad.getTrimmedCanvas();
			if (!trimmed.width || !trimmed.height) {
				return pad.toDataURL(type);
			}

			const out = document.createElement('canvas');
			out.width = trimmed.width + margin * 2;
			out.height = trimmed.height + margin * 2;
			const ctx = out.getContext('2d');
			if (!ctx) {
				return trimmed.toDataURL(type);
			}
			ctx.drawImage(trimmed, margin, margin);

			return out.toDataURL(type);
		},
		clear: () => canvasRef.current?.clear(),
		isEmpty: () => canvasRef.current?.isEmpty() ?? true,
	}));

	return (
		<SignatureCanvas
			ref={canvasRef}
			penColor="black"
			canvasProps={{ width, height, style: { border: '1px solid #d9d9d9', borderRadius: 4 } }}
		/>
	);
});

SignaturePad.displayName = 'SignaturePad';
